import React from 'react';

import styled from 'styled-components';

export interface PanelContentWrapperProps {
  /** Content to display in the panel */
  children: React.ReactNode;
  /** Custom className for styling */
  className?: string;
  /** Content layout mode */
  layout?: 'fill' | 'center' | 'start' | 'end' | 'stretch';
  /** Padding around content */
  padding?: 'none' | 'small' | 'medium' | 'large';
  /** Whether content should be scrollable */
  scrollable?: boolean;
  /** Background color variant */
  background?: 'transparent' | 'white' | 'gray';
  /** Maximum width for centered content */
  maxWidth?: string;
  /** Gap between child elements */
  gap?: 'none' | 'small' | 'medium' | 'large';
  /** Direction for flex layout */
  direction?: 'row' | 'column';
  /** Whether to show a border */
  bordered?: boolean;
}

/**
 * PanelContentWrapper - Automatic CSS layout wrapper for AutoPanelLayout content
 *
 * This component is part of the AutoPanelLayout system and automatically handles:
 * - Flex layout with proper sizing
 * - Scrolling when content overflows
 * - Consistent padding and spacing using theme tokens
 * - Background and border styling
 * - Responsive behavior
 *
 * Use this to wrap any content going into AutoPanelLayout panels
 * to avoid having to think about CSS flex/grid layout.
 */
export const PanelContentWrapper: React.FC<PanelContentWrapperProps> = ({
  children,
  className,
  layout = 'fill',
  padding = 'medium',
  scrollable = true,
  background = 'transparent',
  maxWidth,
  gap = 'small',
  direction = 'column',
  bordered = false,
}) => {
  return (
    <Container
      className={className}
      $layout={layout}
      $padding={padding}
      $scrollable={scrollable}
      $background={background}
      $maxWidth={maxWidth}
      $gap={gap}
      $direction={direction}
      $bordered={bordered}
    >
      {children}
    </Container>
  );
};

// Styled Components with comprehensive layout handling
interface StyledProps {
  $layout: PanelContentWrapperProps['layout'];
  $padding: PanelContentWrapperProps['padding'];
  $scrollable: boolean;
  $background: PanelContentWrapperProps['background'];
  $maxWidth?: string;
  $gap: PanelContentWrapperProps['gap'];
  $direction: PanelContentWrapperProps['direction'];
  $bordered: boolean;
}

const Container = styled.div<StyledProps>`
  /* Essential flex layout */
  display: flex;
  flex-direction: ${({ $direction }) => $direction};
  min-height: 0;
  min-width: 0;

  /* Layout-specific sizing */
  ${({ $layout }) => {
    switch ($layout) {
      case 'fill':
        return `
          flex: 1;
          height: 100%;
          width: 100%;
        `;
      case 'center':
        return `
          flex: 1;
          align-items: center;
          justify-content: center;
        `;
      case 'start':
        return `
          flex: 1;
          align-items: flex-start;
          justify-content: flex-start;
        `;
      case 'end':
        return `
          flex: 1;
          align-items: flex-end;
          justify-content: flex-end;
        `;
      case 'stretch':
        return `
          flex: 1;
          align-items: stretch;
          justify-content: stretch;
        `;
      default:
        return `flex: 1;`;
    }
  }}

  /* Max width constraint for centered layouts */
  ${({ $maxWidth, $layout }) => ($maxWidth && ($layout === 'center' || $layout === 'start' || $layout === 'end') ? `max-width: ${$maxWidth};` : '')}

  /* Padding using theme tokens */
  padding: ${({ theme, $padding }) => {
    switch ($padding) {
      case 'none':
        return '0';
      case 'small':
        return theme.space.spacing10;
      case 'medium':
        return theme.space.spacing20;
      case 'large':
        return theme.space.spacing30;
      default:
        return theme.space.spacing20;
    }
  }};

  /* Gap between children using theme tokens */
  gap: ${({ theme, $gap }) => {
    switch ($gap) {
      case 'none':
        return '0';
      case 'small':
        return theme.space.spacing10;
      case 'medium':
        return theme.space.spacing20;
      case 'large':
        return theme.space.spacing30;
      default:
        return theme.space.spacing10;
    }
  }};

  /* Background styling */
  background: ${({ theme, $background }) => {
    switch ($background) {
      case 'white':
        return theme.colors.palette.white;
      case 'gray':
        return theme.colors.palette.grayscale[1];
      case 'transparent':
      default:
        return 'transparent';
    }
  }};

  /* Border styling */
  ${({ theme, $bordered }) =>
    $bordered
      ? `
    border: 1px solid ${theme.colors.borderBase};
    border-radius: ${theme.radii.base};
  `
      : ''}

  /* Scrolling behavior */
  ${({ $scrollable }) =>
    $scrollable
      ? `
    overflow: auto;
    -webkit-overflow-scrolling: touch; /* iOS smooth scrolling */
  `
      : `
    overflow: visible;
  `}

  /* Ensure child elements can shrink properly */
  > * {
    min-height: 0;
    min-width: 0;
  }

  /* Mobile responsive adjustments */
  @media (max-width: 768px) {
    padding: ${({ theme, $padding }) => {
      switch ($padding) {
        case 'none':
          return '0';
        case 'small':
          return theme.space.spacing10;
        case 'medium':
          return theme.space.spacing10;
        case 'large':
          return theme.space.spacing20;
        default:
          return theme.space.spacing10;
      }
    }};

    gap: ${({ theme, $gap }) => {
      switch ($gap) {
        case 'none':
          return '0';
        case 'small':
          return theme.space.spacing10;
        case 'medium':
          return theme.space.spacing10;
        case 'large':
          return theme.space.spacing20;
        default:
          return theme.space.spacing10;
      }
    }};
  }
`;

PanelContentWrapper.displayName = 'PanelContentWrapper';
